"use client";

import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  CollapseProps,
  ConfigProvider,
  DatePicker,
  Divider,
  Flex,
  Form,
  FormInstance,
  GetProps,
  Skeleton,
  Select,
  Typography,
} from "antd/lib";
import { useTranslation } from "react-i18next";
import { CheckboxOptionType } from "antd/lib/checkbox/Group";
import { useSearchParams } from "next/navigation";
import FadeIn from "@/src/components/Common/FadeIn/FadeIn";
import XStroke from "@/public/icons/XStroke";
import { FilterTypes, SearchParams, SelectOptionsDataType } from "@/constants/common";
import { buildUrlWithParams, getFilterPublicEventTypeLabel, pushSearchParams } from "@/utils/helpers";
import { SelectOptionsResponse } from "@/types/selectOptions";
import { EventsActivityTypes, EventsEquippingLevels } from "@/constants/events";
import dayjs from "dayjs";
import filterConfig from "@/src/theme/filterConfig";
import { useSelectsOptionsFetchers } from "@/hooks/useSelectsOptionsFetchers";
import { usePath } from "@/hooks/usePath";
import FilterWrapper from "@/src/components/Common/FilterWrapper";
import TagCategorySection from "@/src/components/Common/TagCategorySection";
import { FilterFormValues } from "@/types/common";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import Search from "@/src/components/Common/Search";
import { Dayjs } from "dayjs";
import { Item } from "@/types/common";

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>;
type RangePickerValue = [Dayjs, Dayjs] | ["", ""] | null;

const { RangePicker } = DatePicker;
const { Title } = Typography;


interface FilterPublicEventsProps {
  searchRef: React.RefObject<HTMLDivElement | null>;
  dateFilterRef: React.RefObject<HTMLDivElement | null>;
  locationFilterRef: React.RefObject<HTMLDivElement | null>;
  filtersRef: React.RefObject<HTMLDivElement | null>;
  isDrawerOpen: boolean;
  equippingTypeValue?: string;
  setEquippingTypeValue: Dispatch<SetStateAction<string | undefined>>;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  form: FormInstance;
  filtersCleared: boolean;
  searchValue: string;
  showSearchInputReset: boolean;
  applyFiltersHandler: (values: Record<string, string[]>) => void;
  searchInputChangeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchHandler: () => void;
  resetInputSearchHandler: () => void;
  resetFiltersHandler: () => void;
  activityLanguages: CheckboxOptionType[];
  setActivityLanguages: Dispatch<SetStateAction<CheckboxOptionType[]>>;
  activityMaterials: CheckboxOptionType[];
  setActivityMaterials: Dispatch<SetStateAction<CheckboxOptionType[]>>;
  filtersOptionsAreLoading: boolean;
  setFiltersOptionsAreLoading: Dispatch<SetStateAction<boolean>>;
  formValuesChangeHandler: (changedValues: Partial<FilterFormValues>, currentValues: FilterFormValues) => void;
  formValuesChanged: boolean;
  setFiltersOptionsError: Dispatch<SetStateAction<boolean>>;
}

const FilterPublicEvents: React.FC<FilterPublicEventsProps> = ({
  searchRef,
  dateFilterRef,
  locationFilterRef,
  filtersRef,
  isDrawerOpen,
  equippingTypeValue,
  setEquippingTypeValue,
  setIsDrawerOpen,
  form,
  filtersCleared,
  searchValue,
  showSearchInputReset,
  applyFiltersHandler,
  searchInputChangeHandler,
  searchHandler,
  resetInputSearchHandler,
  resetFiltersHandler,
  activityLanguages,
  setActivityLanguages,
  activityMaterials,
  setActivityMaterials,
  filtersOptionsAreLoading,
  setFiltersOptionsAreLoading,
  formValuesChangeHandler,
  formValuesChanged,
  setFiltersOptionsError,
}) => {
  const { t } = useTranslation();
  const pathname = usePath();
  const searchParams = useSearchParams();
  const { fetchSelectOptions } = useSelectsOptionsFetchers();
  const formValues = Form.useWatch([], form);

  const activityType = searchParams.get(SearchParams.filterActivityType);
  const equippingLevel = searchParams.get(SearchParams.filterEquippingLevel);
  const startDate = searchParams.get(SearchParams.filterStartDate);
  const endDate = searchParams.get(SearchParams.filterEndDate);

  const [activityTypes, setActivityTypes] = useState<CheckboxOptionType[]>([]);
  const [rangePickerValue, setRangePickerValue] = useState<RangePickerValue>(null);
  const [countries, setCountries] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    if (!activityTypes.length || !equippingTypeValue) return;

    if (activityType === equippingTypeValue && !equippingLevel) {
      pushSearchParams(
        buildUrlWithParams(pathname, "", {
          ...Object.fromEntries(searchParams),
          [SearchParams.filterEquippingLevel]: `${EventsEquippingLevels.initial},${EventsEquippingLevels.advanced},${EventsEquippingLevels.supplemental},${EventsEquippingLevels.mobileAudio}`,
        }),
      );
    } else if (
      equippingLevel &&
      (equippingLevel.includes(EventsEquippingLevels.initial) ||
        equippingLevel.includes(EventsEquippingLevels.advanced) ||
        equippingLevel.includes(EventsEquippingLevels.supplemental) ||
        equippingLevel.includes(EventsEquippingLevels.mobileAudio)) &&
      !activityType
    ) {
      pushSearchParams(
        buildUrlWithParams(pathname, "", {
          ...Object.fromEntries(searchParams),
          [SearchParams.filterActivityType]: equippingTypeValue,
        }),
      );
    }
  }, [activityType, equippingLevel, activityTypes.length]);

  useEffect(() => {
    if (startDate && endDate) {
      setRangePickerValue([dayjs.utc(startDate?.toString()), dayjs(endDate?.toString())]);
    } else {
      setRangePickerValue(["", ""]);
    }
  }, [startDate, endDate]);

  const transformResponse = (res: SelectOptionsResponse) => {
    return res.data.map((item) => {
      return { label: item.name, value: item.value.toString() };
    });
  };

  const transformActivityTypesResponse = (res: SelectOptionsResponse) => {
    return res.data.flatMap((item) => {
      if (item.name === EventsActivityTypes.equipping) {
        setEquippingTypeValue(item.value);

        return [
          {
            label: t("eventsPage.filterPublicEvents.type.equippingSessionInitial"),
            value: `${item.value}_${EventsEquippingLevels.initial}`,
          },
          {
            label: t("eventsPage.filterPublicEvents.type.equippingSessionAdvanced"),
            value: `${item.value}_${EventsEquippingLevels.advanced}`,
          },
          {
            label: t("eventsPage.filterPublicEvents.type.equippingSessionSupplemental"),
            value: `${item.value}_${EventsEquippingLevels.supplemental}`,
          },
          {
            label: t("eventsPage.filterPublicEvents.type.equippingSessionMobileAudio"),
            value: `${item.value}_${EventsEquippingLevels.mobileAudio}`,
          },
        ];
      }

      return [{ label: getFilterPublicEventTypeLabel(t, item.name), value: item.value }];
    });
  };

  useEffect(() => {
    const fetchFilterOptions = async () => {
      const fetchAndSetActivityTypes = async () => {
        let res: SelectOptionsResponse;

        try {
          res = await fetchSelectOptions({
            dataType: SelectOptionsDataType.activityTypes,
            setIsLoading: setFiltersOptionsAreLoading,
          });
        } catch (err) {
          setFiltersOptionsError(true);
          throw err;
        }

        const transformedRes = transformActivityTypesResponse(res);
        setActivityTypes(transformedRes);
      };

      const fetchAndSetActivityLanguages = async () => {
        let res: SelectOptionsResponse;

        try {
          res = await fetchSelectOptions({
            dataType: SelectOptionsDataType.activityLanguages,
            setIsLoading: setFiltersOptionsAreLoading,
          });
        } catch (err) {
          setFiltersOptionsError(true);
          throw err;
        }

        const transformedRes = transformResponse(res);
        setActivityLanguages(transformedRes);
      };

      const fetchAndSetActivityMaterials = async () => {
        let res: SelectOptionsResponse;

        try {
          res = await fetchSelectOptions({
            dataType: SelectOptionsDataType.materials,
            setIsLoading: setFiltersOptionsAreLoading,
          });
        } catch (err) {
          setFiltersOptionsError(true);
          throw err;
        }

        const transformedRes = transformResponse(res);
        setActivityMaterials(transformedRes);
      };

      const fetchAndSetCountries = async () => {
        const res = await fetchSelectOptions({
          dataType: SelectOptionsDataType.countries,
          setIsLoading: setFiltersOptionsAreLoading,
        });
        if (res) setCountries(res.data.map((c: Item) => ({ label: c.name, value: String(c.value) })));
      };

      await Promise.all([fetchAndSetActivityLanguages(), fetchAndSetActivityTypes(), fetchAndSetActivityMaterials(), fetchAndSetCountries()]);

      setFiltersOptionsAreLoading(false);
    };

    fetchFilterOptions();
  }, []);

  const onlineCheckboxOptions: CheckboxOptionType[] = [
    { label: t("eventsPage.filterPublicEvents.common.online"), value: "1" },
    { label: t("eventsPage.filterPublicEvents.common.inPerson"), value: "0" },
  ];

  const allHandler = (event: CheckboxChangeEvent) => {
    const fieldName = event.target.name;

    if (event.target.checked) {
      const checkboxOptions =
        fieldName === SearchParams.filterOnline
          ? onlineCheckboxOptions
          : fieldName === SearchParams.filterActivityType
            ? activityTypes
            : [];
      if (!checkboxOptions.length) return;

      form.setFieldValue(
        event.target.name,
        checkboxOptions.map((item) => item.value),
      );
    } else {
      form.setFieldValue(event.target.name, []);
    }

    formValuesChangeHandler({ fieldName: form.getFieldValue(fieldName) }, form.getFieldsValue());
  };

  const items: CollapseProps["items"] = [];

  if (activityTypes.length)
    items.push({
      key: "1",
      label: t("eventsPage.filterPublicEvents.type.title"),
      children: (
        <Flex vertical gap={8}>
          <Checkbox
            name={SearchParams.filterActivityType}
            onChange={allHandler}
            checked={formValues?.[SearchParams.filterActivityType]?.length === activityTypes.length}
          >
            {t("eventsPage.filterPublicEvents.common.all")}
          </Checkbox>
          <TagCategorySection name={SearchParams.filterActivityType} items={activityTypes} />
        </Flex>
      ),
    });
  if (activityMaterials.length)
    items.push({
      key: "2",
      label: t("eventsPage.filterPublicEvents.materials.title"),
      children: <TagCategorySection name={SearchParams.filterMaterials} items={activityMaterials} />,
    });
  if (activityLanguages.length)
    items.push({
      key: "3",
      label: t("eventsPage.filterPublicEvents.languages.title"),
      children: <TagCategorySection name={SearchParams.filterLanguage} items={activityLanguages} />,
    });

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    return current && current.isBefore(dayjs(), "day");
  };

  const handleRangePicker = (dates: RangePickerProps["value"], dateStrings: [string, string] | null) => {
    const currentParams = new URLSearchParams(searchParams.toString());

    if (dateStrings?.some((dateString) => dateString.trim() !== "")) {
      currentParams.set(SearchParams.filterStartDate, dateStrings[0]);
      currentParams.set(SearchParams.filterEndDate, dateStrings[1]);
    } else {
      setRangePickerValue(["", ""]);
      currentParams.delete(SearchParams.filterStartDate);
      currentParams.delete(SearchParams.filterEndDate);
    }

    const newUrl = buildUrlWithParams(pathname, "", Object.fromEntries(currentParams.entries()));
    pushSearchParams(newUrl);
  };

  const handleSelectCountry = (value?: string) => {
    const currentParams = new URLSearchParams(searchParams.toString());

    if (value) {
      currentParams.set(SearchParams.filterCountry, value);
    } else {
      currentParams.delete(SearchParams.filterCountry);
    }

    const newUrl = buildUrlWithParams(pathname, "", Object.fromEntries(currentParams.entries()));
    pushSearchParams(newUrl);
  };

  return (
    <FilterWrapper isDrawerOpen={isDrawerOpen} setIsDrawerOpen={setIsDrawerOpen}>
      <ConfigProvider theme={filterConfig}>
        <div className="filter">
          <Card>
            <Skeleton active loading={filtersOptionsAreLoading}>
              <FadeIn type="top">
                <Flex vertical gap={16}>
                  <Flex vertical gap={8} style={{ transform: "translateY(2px)" }} ref={searchRef}>
                    <Search filterType={FilterTypes.common} />
                  </Flex>
                  <div ref={dateFilterRef}>
                    <RangePicker
                      disabledDate={disabledDate}
                      value={rangePickerValue as RangePickerProps["value"]}
                      onChange={handleRangePicker}
                      placeholder={[
                        t("eventsPage.filterPublicEvents.date.from"),
                        t("eventsPage.filterPublicEvents.date.to"),
                      ]}
                    />
                  </div>
                  <div ref={locationFilterRef}>
                    <Select
                      showSearch
                      className="w-full"
                      placeholder={t("eventsPage.filterPublicEvents.country.placeholder")}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        !!option && option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA, optionB) =>
                        (optionA?.label ?? "").toLowerCase().localeCompare((optionB?.label ?? "").toLowerCase())
                      }
                      options={countries}
                      allowClear
                      loading={filtersOptionsAreLoading}
                      value={searchParams.get(SearchParams.filterCountry) ?? undefined}
                      onChange={handleSelectCountry}
                      onClear={() => handleSelectCountry(undefined)}
                    />
                  </div>
                </Flex>
                <Divider />
                <div ref={filtersRef}>
                  <Form form={form} onFinish={applyFiltersHandler} onValuesChange={formValuesChangeHandler}>
                    <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 24 }}>
                      <Title level={5} style={{ margin: 0, flexShrink: 0 }}>
                        {t("common.filters.title")}
                      </Title>
                      <Button block type="primary" htmlType="submit" disabled={!formValuesChanged}>
                        {t("common.filters.actions.apply")}
                      </Button>
                    </Flex>
                    <Button
                      disabled={filtersCleared}
                      type="text"
                      onClick={resetFiltersHandler}
                      style={{
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        marginBottom: 8,
                      }}
                      icon={<XStroke color={filtersCleared ? "#B0B0B0" : undefined} />}
                    >
                      {t("common.filters.actions.clear")}
                    </Button>
                    <Flex vertical gap={12}>
                      <Flex vertical gap={8}>
                        <Checkbox
                          name={SearchParams.filterOnline}
                          onChange={allHandler}
                          checked={formValues?.[SearchParams.filterOnline]?.length === onlineCheckboxOptions.length}
                        >
                          {t("eventsPage.filterPublicEvents.common.all")}
                        </Checkbox>
                        <TagCategorySection name={SearchParams.filterOnline} items={onlineCheckboxOptions} />
                      </Flex>
                      <Collapse ghost items={items} defaultActiveKey={["1", "2", "3"]} />
                    </Flex>
                  </Form>
                </div>
              </FadeIn>
            </Skeleton>
          </Card>
        </div>
      </ConfigProvider>
    </FilterWrapper>
  );
};

export default FilterPublicEvents;
